import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { trpc } from "@/lib/trpc";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

type NotificationRow = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  readAt: Date | string | null;
  createdAt: Date | string;
};

export function NotificationBell() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const list = trpc.notifications.list.useQuery(
    { limit: 20 },
    { refetchInterval: 10000 }
  );
  const unread = trpc.notifications.unreadCount.useQuery(undefined, {
    refetchInterval: 10000,
  });

  const markRead = trpc.notifications.markRead.useMutation({
    onSuccess: () => {
      utils.notifications.list.invalidate();
      utils.notifications.unreadCount.invalidate();
    },
  });
  const markAllRead = trpc.notifications.markAllRead.useMutation({
    onSuccess: () => {
      utils.notifications.list.invalidate();
      utils.notifications.unreadCount.invalidate();
    },
  });

  const items = (list.data as NotificationRow[] | undefined) ?? [];
  const unreadCount = unread.data?.count ?? 0;

  const onClickItem = (n: NotificationRow) => {
    if (!n.readAt) markRead.mutate({ id: n.id });
    const data = (n.data && typeof n.data === "object" ? n.data : {}) as Record<
      string,
      unknown
    >;
    const opportunityId = "opportunityId" in data ? String(data.opportunityId) : null;
    if (opportunityId) {
      navigate(`/o/${opportunityId}`);
      return;
    }
    const clientId = "clientId" in data ? String(data.clientId) : null;
    if (clientId) navigate(`/clients/${clientId}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              className="text-xs font-normal text-blue-600 hover:underline"
            >
              Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            No notifications
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {items.map((n) => (
              <DropdownMenuItem
                key={n.id}
                onClick={() => onClickItem(n)}
                className={cn(
                  "flex flex-col items-start gap-0.5 whitespace-normal py-2",
                  !n.readAt && "bg-blue-50/60"
                )}
              >
                <div className="flex w-full items-baseline justify-between gap-2">
                  <div className="text-sm font-medium leading-tight">{n.title}</div>
                  {!n.readAt && (
                    <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                  )}
                </div>
                {n.body && (
                  <div className="line-clamp-2 text-xs text-muted-foreground">{n.body}</div>
                )}
                <div className="text-[10px] text-muted-foreground/60">
                  {formatRelative(n.createdAt)}
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function formatRelative(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay} d ago`;
  return date.toLocaleDateString("en-US");
}
